/**
 * Содержимое панели детализации.
 *
 * Переход вниз — тот же отчёт с фильтром по выбранной строке и разрезом
 * по следующему измерению. Каждый шаг можно продолжить кликом по строке
 * разбивки; хлебные крошки возвращают наверх.
 */

import { Icon } from '../components/Icon'
import { useEffect, useState, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { useFinance, type DrillTarget } from './context'
import { indexBy, num, rowKey, useReport } from './data'
import { BarRows, ColumnChart, SegmentBar } from './charts'
import { Panel, SkeletonColumns, SkeletonRows } from './parts'
import { BUCKETS, DEBT_METRICS, useSalesSource } from './queries'
import { labelOf, monthLabel, monthsOf, shortMonth, type DateRange } from './period'
import { missingOf } from './coverage'
import { CASH_SERIES } from './sections/Cash'
import { DIMENSION_TITLES, SALES_SERIES, money, overdueOf } from './sections/Overview'
import { formatMoneyShort, formatPercent, toNumber } from '../format'

const DRILL_DIMENSIONS = ['direction', 'nomenclature_group', 'branch'] as const

const SINGULAR: Record<string, string> = {
  direction: 'Направление',
  nomenclature_group: 'Номенклатурная группа',
  branch: 'Филиал',
}

interface Step {
  dimension: string
  value: string
  label: string
}

export function DrillContent({ target }: { target: DrillTarget }) {
  if (target.kind === 'sales') return <SalesDrill target={target} />
  if (target.kind === 'customer') return <CustomerDrill value={target.value} />
  if (target.kind === 'cash_item') return <CashItemDrill value={target.value} />
  return <DebtorDrill value={target.value} />
}

export function drillTitle(target: DrillTarget): string {
  return target.label
}

export function drillSubtitle(target: DrillTarget): string {
  if (target.kind === 'sales') return SINGULAR[target.dimension] ?? ''
  if (target.kind === 'customer') return 'Клиент'
  if (target.kind === 'cash_item') return 'Статья ДДС'
  return 'Должник'
}

/**
 * Ссылка в отчёт с тем же срезом: фильтры и явный период. Без периода
 * отчёт открывался «за 12 месяцев от сегодня», и при отстающей выгрузке
 * показывал другие цифры или пустую таблицу.
 */
function reportLink(code: string, groupBy: string, filters: Record<string, string[]>, range?: DateRange) {
  const params = new URLSearchParams({ g: groupBy })
  if (range) {
    params.set('from', range.from)
    params.set('to', range.to)
  } else params.set('p', 'year')
  for (const [key, values] of Object.entries(filters)) params.set(`f.${key}`, values.join(','))
  return `/reports/${code}?${params.toString()}`
}

function SalesDrill({ target }: { target: Extract<DrillTarget, { kind: 'sales' }> }) {
  const { range, trend, selection, branch, scoped, catalog, coverage } = useFinance()
  const source = useSalesSource()
  const [steps, setSteps] = useState<Step[]>([{ dimension: target.dimension, value: target.value, label: target.label }])

  useEffect(() => {
    setSteps([{ dimension: target.dimension, value: target.value, label: target.label }])
  }, [target.dimension, target.value, target.label])

  const filters = Object.fromEntries(steps.map((step) => [step.dimension, [step.value]]))
  const used = new Set(steps.map((step) => step.dimension))
  const options = DRILL_DIMENSIONS.filter(
    (key) => !used.has(key) && !(key === 'branch' && branch) && source && catalog.hasDimension(source.code, key),
  )
  const [next, setNext] = useState<string | null>(null)
  const dimension = next && options.includes(next as (typeof DRILL_DIMENSIONS)[number]) ? next : (options[0] ?? null)

  const code = source?.code ?? 'margin_by_department'
  const metrics = source?.metrics ?? ['revenue']
  const breakdown = useReport(code, source && dimension ? scoped(code, { period: range, group_by: [dimension], metrics, filters }) : null)
  const totalsState = useReport(code, source ? scoped(code, { period: range, group_by: ['period'], metrics, filters }) : null)
  const monthly = useReport(code, source ? scoped(code, { period: trend, group_by: ['period'], metrics, filters }) : null)

  const months = monthsOf(trend)
  const byMonth = indexBy(monthly.data?.rows, 'period')
  const rangeMonths = new Set(monthsOf(range))
  const totals = totalsState.data?.totals ?? {}
  const rows = [...(breakdown.data?.rows ?? [])].filter((row) => num(row.revenue) !== 0).sort((a, b) => num(b.revenue) - num(a.revenue))
  const withMargin = source?.withMargin ?? false
  const last = steps[steps.length - 1]!

  return (
    <div className="drill">
      {steps.length > 1 && (
        <nav className="crumbs" aria-label="Путь детализации">
          {steps.map((step, index) => (
            <span key={`${step.dimension}-${step.value}`} className="crumbs__item">
              {index > 0 && <Icon name="chevron-right" size={12} className="crumbs__sep" />}
              {index < steps.length - 1 ? (
                <button type="button" className="link-button" onClick={() => setSteps(steps.slice(0, index + 1))}>
                  {step.label}
                </button>
              ) : (
                <span className="strong">{step.label}</span>
              )}
            </span>
          ))}
        </nav>
      )}

      <div key={steps.map((step) => step.value).join('/')} className="drill-step stack" style={{ gap: 18 }}>
        <div className="drill-kpis">
          {[
            { label: 'Выручка без НДС', value: toNumber(totals.revenue), format: money },
            ...(withMargin
              ? [
                  { label: 'Валовая прибыль', value: toNumber(totals.gross_profit), format: money },
                  { label: 'Маржа', value: toNumber(totals.margin_percent), format: (value: number) => formatPercent(value) },
                ]
              : []),
          ].map((item, index) => (
            <div key={item.label} className="drill-kpi cascade" style={{ '--i': index } as CSSProperties}>
              <div className="tiny muted">{item.label}</div>
              {totalsState.status === 'loading' ? (
                <div className="skeleton" style={{ height: 22, width: '70%', marginTop: 6 }} />
              ) : (
                <div className="drill-kpi__value mono">{item.value === null ? '—' : item.format(item.value)}</div>
              )}
            </div>
          ))}
        </div>
        <div className="tiny muted" style={{ marginTop: -8 }}>
          {labelOf(selection)} · {steps.map((step) => step.label).join(' · ')}
        </div>

        <Panel title="По месяцам" subtitle="12 месяцев" states={[monthly]} skeleton={<SkeletonColumns height={140} />}>
          {() => (
            <ColumnChart
              ariaLabel={`${last.label}: по месяцам`}
              height={140}
              series={withMargin ? SALES_SERIES : SALES_SERIES.slice(0, 1)}
              summaryTitle={labelOf(selection)}
              points={months.map((month) => ({
                key: month,
                label: shortMonth(month),
                title: monthLabel(month),
                highlight: rangeMonths.has(month),
                missing: missingOf(month, coverage),
                values: withMargin ? [num(byMonth.get(month)?.revenue), num(byMonth.get(month)?.gross_profit)] : [num(byMonth.get(month)?.revenue)],
              }))}
            />
          )}
        </Panel>

        {dimension ? (
          <Panel
            title={`Разбивка: ${DIMENSION_TITLES[dimension]?.toLowerCase()}`}
            subtitle="Строка — шаг глубже"
            states={[breakdown]}
            skeleton={<SkeletonRows rows={4} />}
            empty={{ when: rows.length === 0, title: 'Нечего разбивать', hint: 'За период по этому срезу продаж нет.' }}
            actions={
              options.length > 1 ? (
                <select className="select select--sm" value={dimension} onChange={(event) => setNext(event.target.value)} aria-label="Разбивка">
                  {options.map((key) => (
                    <option key={key} value={key}>
                      {DIMENSION_TITLES[key]}
                    </option>
                  ))}
                </select>
              ) : undefined
            }
          >
            {() => (
              <BarRows
                ariaLabel="Разбивка"
                series={withMargin ? SALES_SERIES : SALES_SERIES.slice(0, 1)}
                rows={rows.slice(0, 10).map((row) => ({
                  key: rowKey(row, dimension),
                  label: String(row[dimension] ?? '—'),
                  sub: withMargin ? `маржа ${formatPercent(row.margin_percent)}` : undefined,
                  values: withMargin ? [num(row.revenue), toNumber(row.gross_profit)] : [num(row.revenue)],
                }))}
                onSelect={(row) => {
                  setSteps([...steps, { dimension, value: row.key, label: row.label }])
                  setNext(null)
                }}
              />
            )}
          </Panel>
        ) : (
          <div className="small muted">Глубже разбивать нечем: все разрезы уже в пути.</div>
        )}

        <Link className="small" to={reportLink(code, dimension ?? 'period', branch ? { ...filters, branch: [branch] } : filters, range)}>
          Открыть в отчёте с этими фильтрами <Icon name="arrow-right" size={14} />
        </Link>
      </div>
    </div>
  )
}

function CustomerDrill({ value }: { value: string }) {
  const { trend, range, branch, scoped, selection, coverage } = useFinance()
  const filters = { counterparty: [value] }
  const metrics = ['revenue', 'gross_profit', 'margin_percent']
  const monthly = useReport('abc_customers', scoped('abc_customers', { period: trend, group_by: ['period'], metrics, filters }))
  const byBranch = useReport('abc_customers', branch ? null : scoped('abc_customers', { period: range, group_by: ['branch'], metrics, filters }))
  const months = monthsOf(trend)
  const byMonth = indexBy(monthly.data?.rows, 'period')
  const rangeMonths = new Set(monthsOf(range))
  const hasMargin = monthly.data?.columns.some((column) => column.key === 'gross_profit') ?? false
  const branches = [...(byBranch.data?.rows ?? [])].sort((a, b) => num(b.revenue) - num(a.revenue))

  return (
    <div className="drill-step stack" style={{ gap: 18 }}>
      <Panel title="Выручка по месяцам" subtitle="12 месяцев" states={[monthly]} skeleton={<SkeletonColumns height={150} />}>
        {() => (
          <ColumnChart
            ariaLabel="Выручка клиента по месяцам"
            height={150}
            series={hasMargin ? SALES_SERIES : SALES_SERIES.slice(0, 1)}
            summaryTitle={labelOf(selection)}
            points={months.map((month) => ({
              key: month,
              label: shortMonth(month),
              title: monthLabel(month),
              highlight: rangeMonths.has(month),
              missing: missingOf(month, coverage),
              values: hasMargin ? [num(byMonth.get(month)?.revenue), num(byMonth.get(month)?.gross_profit)] : [num(byMonth.get(month)?.revenue)],
            }))}
          />
        )}
      </Panel>
      {!branch && (
        <Panel title="По филиалам" subtitle="За выбранный период" states={[byBranch]} skeleton={<SkeletonRows rows={3} />} empty={{ when: branches.length === 0, title: 'Продаж за период нет' }}>
          {() => (
            <BarRows
              ariaLabel="Выручка клиента по филиалам"
              series={SALES_SERIES.slice(0, 1)}
              rows={branches.map((row) => ({ key: rowKey(row, 'branch'), label: String(row.branch ?? '—'), sub: hasMargin ? `маржа ${formatPercent(row.margin_percent)}` : undefined, values: [num(row.revenue)] }))}
            />
          )}
        </Panel>
      )}
      <Link className="small" to={reportLink('abc_customers', 'period', branch ? { ...filters, branch: [branch] } : filters, trend)}>
        Открыть в отчёте <Icon name="arrow-right" size={14} />
      </Link>
    </div>
  )
}

function CashItemDrill({ value }: { value: string }) {
  const { trend, range, scoped, selection } = useFinance()
  const filters = { cashflow_item: [value] }
  const metrics = ['inflow', 'outflow', 'net_flow']
  const monthly = useReport('cashflow', scoped('cashflow', { period: trend, group_by: ['period'], metrics, filters }))
  const byOrg = useReport('cashflow', scoped('cashflow', { period: range, group_by: ['organization'], metrics, filters }))
  const months = monthsOf(trend)
  const byMonth = indexBy(monthly.data?.rows, 'period')
  const rangeMonths = new Set(monthsOf(range))
  const orgs = [...(byOrg.data?.rows ?? [])].sort((a, b) => num(b.inflow) + num(b.outflow) - num(a.inflow) - num(a.outflow))
  const outgoing = num(monthly.data?.totals.outflow) >= num(monthly.data?.totals.inflow)
  const series = outgoing ? [CASH_SERIES[1]!] : [CASH_SERIES[0]!]
  const key = outgoing ? 'outflow' : 'inflow'

  return (
    <div className="drill-step stack" style={{ gap: 18 }}>
      <Panel title={outgoing ? 'Списания по месяцам' : 'Поступления по месяцам'} subtitle="12 месяцев · вся компания" states={[monthly]} skeleton={<SkeletonColumns height={150} />}>
        {() => (
          <ColumnChart
            ariaLabel="Движение по статье по месяцам"
            height={150}
            series={series}
            summaryTitle={labelOf(selection)}
            points={months.map((month) => ({ key: month, label: shortMonth(month), title: monthLabel(month), highlight: rangeMonths.has(month), values: [num(byMonth.get(month)?.[key])] }))}
          />
        )}
      </Panel>
      <Panel title="По организациям" subtitle="За выбранный период" states={[byOrg]} skeleton={<SkeletonRows rows={3} />} empty={{ when: orgs.length === 0, title: 'Движений за период нет' }}>
        {() => (
          <BarRows
            ariaLabel="Движение по организациям"
            series={series}
            rows={orgs.map((row) => ({ key: rowKey(row, 'organization'), label: String(row.organization ?? '—'), values: [num(row[key])] }))}
          />
        )}
      </Panel>
      <Link className="small" to={reportLink('cashflow', 'period', filters, trend)}>
        Открыть в отчёте <Icon name="arrow-right" size={14} />
      </Link>
    </div>
  )
}

function DebtorDrill({ value }: { value: string }) {
  const { scoped, receivableFilter } = useFinance()
  // Признак «внешние / между своими» определяется самим контрагентом:
  // фильтр по нему скрыл бы долг своего ИП, открытый из списка «между своими».
  const { relation: _relation, ...base } = receivableFilter
  const filters = { ...base, counterparty: [value] }
  const byOrg = useReport('receivables', scoped('receivables', { group_by: ['organization'], metrics: DEBT_METRICS, filters }))
  const totals = byOrg.data?.totals ?? {}
  const amount = num(totals.amount)
  const orgs = [...(byOrg.data?.rows ?? [])].sort((a, b) => num(b.amount) - num(a.amount))

  return (
    <div className="drill-step stack" style={{ gap: 18 }}>
      <Panel title="Долг по срокам" subtitle="На последнюю дату" states={[byOrg]} skeleton={<SkeletonRows rows={4} height={14} />}>
        {() => (
          <div className="stack" style={{ gap: 14 }}>
            <div className="drill-kpis">
              <div className="drill-kpi">
                <div className="tiny muted">Долг</div>
                <div className="drill-kpi__value mono">{money(amount)}</div>
              </div>
              <div className="drill-kpi">
                <div className="tiny muted">Просрочено</div>
                <div className="drill-kpi__value mono" style={{ color: overdueOf(totals) > 0 ? 'var(--critical)' : undefined }}>
                  {money(overdueOf(totals))}
                </div>
              </div>
            </div>
            <SegmentBar ariaLabel="Долг по срокам" segments={BUCKETS.map((bucket) => ({ key: bucket.key, label: bucket.label, color: bucket.color, value: num(totals[bucket.key]) }))} />
            <ul className="ageing-legend">
              {BUCKETS.map((bucket) => (
                <li key={bucket.key}>
                  <span className="chart-legend__swatch" style={{ background: bucket.color }} aria-hidden="true" />
                  <span className="secondary">{bucket.label}</span>
                  <span className="mono strong">{formatMoneyShort(totals[bucket.key])}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Panel>
      {orgs.length > 1 && (
        <Panel title="По нашим организациям" subtitle="Кому из четырёх юрлиц должен контрагент" states={[byOrg]}>
          {() => (
            <BarRows
              ariaLabel="Долг по организациям"
              series={[{ key: 'amount', label: 'Долг', color: 'var(--series-1)' }]}
              rows={orgs.map((row) => ({ key: rowKey(row, 'organization'), label: String(row.organization ?? '—'), values: [num(row.amount)] }))}
            />
          )}
        </Panel>
      )}
      <Link className="small" to={reportLink('receivables', 'organization', filters)}>
        Открыть в отчёте <Icon name="arrow-right" size={14} />
      </Link>
    </div>
  )
}
